import React, { useEffect, useRef, useState } from 'react';
import { aiApi, type AITutorMessage } from '../../services/aiApi';
import { typesetElement } from '../../lib/mathjax';
import { DraggableResizablePanel } from '../common/DraggableResizablePanel';

interface Props {
  /** 截图 PNG dataURL（data:image/png;base64,....） */
  imageDataUrl: string;
  onClose: () => void;
}

interface ChatBubble {
  role: 'user' | 'model';
  text: string;
  image?: string;     // 仅首条用户气泡带截图
  hideText?: boolean; // 隐藏气泡里的文字（首条自动提问仍发给 AI，但不在界面显示）
}

/** data:image/png;base64,XXXX -> { mimeType, base64 } */
function splitDataUrl(dataUrl: string): { mimeType: string; base64: string } {
  const m = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
  if (m) return { mimeType: m[1], base64: m[2] };
  return { mimeType: 'image/png', base64: dataUrl };
}

/**
 * 极简 markdown -> HTML（覆盖 AI 回复常用语法：## 标题、- 列表、**粗体**、空行分段），
 * 数学 \( \) / \[ \] 原样保留，交给 MathJax typeset。
 */
function renderMarkdown(src: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // 先转义，再恢复我们支持的内联标记（粗体）
  const inline = (s: string) =>
    escape(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  const lines = src.replace(/\r/g, '').split('\n');
  const html: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*##\s+/.test(line)) {
      html.push(`<h4 class="font-semibold text-gray-800 mt-2 mb-1">${inline(line.replace(/^\s*##\s+/, ''))}</h4>`);
      i++;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ''))}</li>`);
        i++;
      }
      html.push(`<ul class="list-disc pl-5 space-y-0.5">${items.join('')}</ul>`);
      continue;
    }
    if (line.trim() === '') { i++; continue; }
    // 普通段落：合并连续非空行
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^\s*(##\s+|[-*]\s+)/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    html.push(`<p class="leading-relaxed">${inline(para.join(' '))}</p>`);
  }
  return html.join('');
}

const AIMessage: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = renderMarkdown(text);
    typesetElement(el);
  }, [text]);
  return <div ref={ref} className="prose max-w-none text-sm text-gray-800 space-y-1" />;
};

export const AITutorModal: React.FC<Props> = ({ imageDataUrl, onClose }) => {
  const [messages, setMessages] = useState<ChatBubble[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentFirst = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { base64, mimeType } = splitDataUrl(imageDataUrl);

  // 把前端气泡历史转成后端需要的 messages（纯文本角色序列）
  const toApiHistory = (bubbles: ChatBubble[]): AITutorMessage[] =>
    bubbles.map((b) => ({ role: b.role, text: b.text }));

  const send = async (text: string, isFirst: boolean) => {
    setLoading(true);
    setError(null);
    const userBubble: ChatBubble = isFirst
      ? { role: 'user', text, image: imageDataUrl, hideText: true }
      : { role: 'user', text };
    const next = [...messages, userBubble];
    setMessages(next);
    try {
      // 首轮带图片；后续只带历史文本
      const reply = await aiApi.tutor(toApiHistory(next), isFirst ? base64 : undefined, mimeType);
      setMessages((prev) => [...prev, { role: 'model', text: reply }]);
    } catch (e: any) {
      setError(e?.message || 'AI request failed');
    } finally {
      setLoading(false);
    }
  };

  // 首次打开：自动用截图发起第一轮
  useEffect(() => {
    if (sentFirst.current) return;
    sentFirst.current = true;
    void send('Please analyze this question. If my answer is wrong, explain the mistake and guide me through the correct approach.', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = () => {
    const t = input.trim();
    if (!t || loading) return;
    setInput('');
    void send(t, false);
  };

  return (
    <DraggableResizablePanel
      title={<span className="text-sm font-semibold text-gray-800">AI Tutor</span>}
      onClose={onClose}
      initialWidth="min(560px, 95vw)"
      initialHeight="min(82vh, 760px)"
      minWidth={320}
      minHeight={360}
      zIndex={60}
    >
      {/* 对话区 */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-primary-50/20">
        {messages.map((m, idx) => (
          <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[82%] rounded-2xl px-3 py-2 ${
                m.role === 'user' ? 'bg-primary-500 text-white rounded-br-sm' : 'bg-white border border-gray-100 rounded-bl-sm'
              }`}
            >
              {m.image && (
                <img src={m.image} alt="question" className="rounded-lg max-h-40 object-contain border border-white/30" />
              )}
              {m.role === 'user'
                ? (!m.hideText && <span className="text-sm whitespace-pre-wrap break-words">{m.text}</span>)
                : <AIMessage text={m.text} />}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-400">
              Thinking…
            </div>
          </div>
        )}
        {error && <div className="text-xs text-red-500 text-center">{error}</div>}
        <div ref={bottomRef} />
      </div>

      {/* 输入区 */}
      <div className="flex items-center gap-2 px-3 h-14 shrink-0 border-t border-gray-100">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Ask a follow-up question…"
          disabled={loading}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
        />
        <button
          onClick={handleSend}
          disabled={loading || !input.trim()}
          className="px-3 py-2 rounded-md bg-primary-500 text-white text-sm hover:bg-primary-600 disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </DraggableResizablePanel>
  );
};
